// ═══════════════════════════════════════════════════════════════
// lazyWithReload — ChunkLoadError auto-recovery
//
// After a deploy, the old content-hashed JS chunks are gone. An
// already-open tab that navigates to a not-yet-loaded React.lazy
// view fetches a chunk that 404s → ChunkLoadError → the red
// ErrorBoundary panel ("must hard-refresh"). This wraps React.lazy
// so that the FIRST such failure silently reloads the page (which
// pulls the fresh index.html + new chunk names). A sessionStorage
// one-shot guard prevents an infinite reload loop: if we've already
// reloaded once this tab session, we rethrow and let the
// ErrorBoundary show the panel.
// ═══════════════════════════════════════════════════════════════

import { lazy, type ComponentType } from 'react';
import { CHUNK_RELOAD_GUARD_KEY } from './version';

export function isChunkLoadError(err: unknown): boolean {
  const e = err as { name?: string; message?: string } | null;
  const name = e?.name ?? '';
  const message = e?.message ?? '';
  return (
    name === 'ChunkLoadError' ||
    /Failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message)
  );
}

function alreadyReloaded(): boolean {
  try { return sessionStorage.getItem(CHUNK_RELOAD_GUARD_KEY) === '1'; }
  catch { return false; }
}

function markReloaded(): void {
  try { sessionStorage.setItem(CHUNK_RELOAD_GUARD_KEY, '1'); } catch { /* ignore */ }
}

export function lazyWithReload<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(async () => {
    try {
      return await factory();
    } catch (err) {
      if (isChunkLoadError(err) && !alreadyReloaded()) {
        markReloaded();
        window.location.reload();
        // Hang so React keeps showing the Suspense fallback (spinner)
        // through the reload, rather than surfacing the error panel.
        return new Promise<{ default: T }>(() => { /* never resolves */ });
      }
      throw err; // already reloaded once, or not a chunk error → ErrorBoundary
    }
  });
}
